import React, { useCallback, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import { t } from "../i18n";

/** Addis Ababa - sensible default for Ethiopian merchants */
const DEFAULT_CENTER = [9.032, 38.746];
const DEFAULT_ZOOM = 13;

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 15000, maximumAge: 60_000 };

function coordsAreValidPin(latNum, lngNum) {
  return (
    latNum != null &&
    lngNum != null &&
    latNum >= -90 &&
    latNum <= 90 &&
    lngNum >= -180 &&
    lngNum <= 180
  );
}

let leafletDefaultIconsFixed = false;

function formatCoord(n) {
  return String(Number(Number(n).toFixed(6)));
}

function parseCoordString(s) {
  const n = Number(String(s ?? "").trim());
  if (!Number.isFinite(n)) return null;
  return n;
}

/** Fix default marker icons under Webpack (CRA). */
function ensureLeafletIcons() {
  if (leafletDefaultIconsFixed) return;
  leafletDefaultIconsFixed = true;
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  });
}

function MapRecenter({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (lat == null || lng == null) return;
    map.setView([lat, lng], zoom ?? map.getZoom(), { animate: true, duration: 0.25 });
  }, [lat, lng, zoom, map]);
  return null;
}

function MapPickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/**
 * OpenStreetMap picker: click map or drag marker; requests browser geolocation once on mount when
 * coordinates are empty so the pin defaults to the user's current location when permitted.
 * Controlled by `latitude` / `longitude` string props (same format as form inputs).
 */
export function OfficeLocationPickerMap({ latitude, longitude, onLocationChange }) {
  ensureLeafletIcons();

  const latestCoords = useRef({ latitude, longitude });
  latestCoords.current = { latitude, longitude };

  const onLocationChangeRef = useRef(onLocationChange);
  onLocationChangeRef.current = onLocationChange;

  const latNum = parseCoordString(latitude);
  const lngNum = parseCoordString(longitude);
  const hasValidPin = coordsAreValidPin(latNum, lngNum);

  /** First load with empty fields: try GPS so the pin starts at the user's location (registration UX). */
  useEffect(() => {
    if (!navigator.geolocation) return;
    const { latitude: la, longitude: lo } = latestCoords.current;
    if (coordsAreValidPin(parseCoordString(la), parseCoordString(lo))) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const curLat = parseCoordString(latestCoords.current.latitude);
        const curLng = parseCoordString(latestCoords.current.longitude);
        if (coordsAreValidPin(curLat, curLng)) return;
        onLocationChangeRef.current(
          formatCoord(pos.coords.latitude),
          formatCoord(pos.coords.longitude)
        );
      },
      () => {},
      GEO_OPTIONS
    );
  }, []);

  const applyPick = useCallback(
    (rawLat, rawLng) => {
      if (!Number.isFinite(rawLat) || !Number.isFinite(rawLng)) return;
      if (rawLat < -90 || rawLat > 90 || rawLng < -180 || rawLng > 180) return;
      onLocationChange(formatCoord(rawLat), formatCoord(rawLng));
    },
    [onLocationChange]
  );

  function requestUserLocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        applyPick(pos.coords.latitude, pos.coords.longitude);
      },
      () => {
        /* Browser surfaces permission errors; optional UI can be added later */
      },
      GEO_OPTIONS
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      <div
        style={{
          borderRadius: 14,
          overflow: "hidden",
          border: "1px solid #e6e1f0",
          height: 260,
          background: "#fcfbfe",
        }}
      >
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: "100%", width: "100%", zIndex: 0 }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapPickHandler onPick={applyPick} />
          {hasValidPin ? (
            <>
              <MapRecenter lat={latNum} lng={lngNum} zoom={16} />
              <Marker
                position={[latNum, lngNum]}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const p = e.target.getLatLng();
                    applyPick(p.lat, p.lng);
                  },
                }}
              />
            </>
          ) : (
            <MapRecenter lat={DEFAULT_CENTER[0]} lng={DEFAULT_CENTER[1]} zoom={DEFAULT_ZOOM} />
          )}
        </MapContainer>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginTop: 8 }}>
        <span style={{ fontSize: 11, color: "#8c8595", flex: "1 1 200px" }}>
          {t("officemap.hint")}
        </span>
        <button
          type="button"
          onClick={requestUserLocation}
          style={{
            padding: "6px 12px",
            fontSize: 12,
            borderRadius: 10,
            border: "1px solid #cfc7e0",
            background: "#fcfbfe",
            color: "#8467c2",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          {t("ready.use_my_location")}
        </button>
      </div>
    </div>
  );
}
